// 材料マスタ(material-masters)の fetch + byId Map + nameOf を共通化する。
// 3ページ(CarryInPage/CarryOutPage/MaterialSearchPage)で重複していた無検証 as キャストを
// getRowsChecked による safeParse へ揃える。取得失敗時は console.warn + showError。
import { useEffect, useMemo, useState } from "react";
import { AuthedApi } from "../api/Api";
import { MaterialMasterBase, MaterialMasterBaseSchema } from "../schema/generated/MaterialMaster";
import { getRowsChecked } from "./getRowsChecked";
import { useIsOnline } from "./useIsOnline";

export type MaterialMastersById = Map<number, MaterialMasterBase>;

export function useMaterialMasters(api: AuthedApi): {
  materials: MaterialMasterBase[];
  materialsById: MaterialMastersById;
  nameOf: (id: number | null | undefined) => string;
} {
  const [materials, setMaterials] = useState<MaterialMasterBase[]>([]);
  const isOnline = useIsOnline(api);

  useEffect(() => {
    // オフライン時は取得しない。getRowsFromServer は IDB フォールバックが無く、オフライン閲覧の
    // たびに失敗トーストが出るため。オンライン復帰時(isOnline 変化)に取得する。
    if (!isOnline) {
      return;
    }
    let active = true;
    getRowsChecked(api, "material-masters", MaterialMasterBaseSchema.base)
      .then((rows) => { if (active) { setMaterials(rows); } })
      .catch((e) => {
        console.warn("material-masters fetch failed", e);
        if (active) {
          api.showError(`材料マスタの取得に失敗しました。\n${e?.message ?? e}`);
        }
      });
    return () => { active = false; };
  }, [api, isOnline]);

  const materialsById = useMemo(() => {
    const map: MaterialMastersById = new Map();
    materials.forEach((m) => map.set(m.id, m));
    return map;
  }, [materials]);

  const nameOf = (id: number | null | undefined): string => {
    if (id == null) {
      return "-";
    }
    return materialsById.get(id)?.name ?? `#${id}`;
  };

  return { materials, materialsById, nameOf };
}
